import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { SidebarProvider, Sidebar, SidebarInset } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

import { horizonScanningData } from '@/mocks/horizonScanningData';
import { HorizonScanningHeader } from '@/components/horizon-scanning/HorizonScanningHeader';
import { HorizonScanningGrid } from '@/components/horizon-scanning/HorizonScanningGrid';

export default function HorizonScanning() {
  const [searchQuery, setSearchQuery] = useState('');
  const [impactFilter, setImpactFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');

  const handleUploadClick = () => {
    toast.info('Upload Policy or Regulation', {
      description: 'Upload functionality would open here. This is a placeholder.',
      duration: 3000,
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredItems = horizonScanningData.items.filter(item => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      item.section.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.source.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());

    // Impact filter
    const matchesImpact = impactFilter === 'all' || item.recommendations.priorityLevel === impactFilter;

    // Domain filter
    const matchesDomain = domainFilter === 'all' || item.domain === domainFilter;

    return matchesSearch && matchesImpact && matchesDomain;
  });

  return (
    <SidebarProvider>
      <Helmet>
        <title>Horizon Scanning - Regulatory Compliance Monitoring</title>
        <meta name="description" content="Monitor regulatory changes and compliance requirements with horizon scanning dashboard" />
      </Helmet>

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <Sidebar variant="sidebar" className="border-r">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-6">Verdea Compliance</h2>
            <nav className="space-y-2">
              <Button variant="ghost" className="w-full justify-start">
                Admin Users
              </Button>
              <Button variant="ghost" className="w-full justify-start">
                Compliance Status
              </Button>
              <Button variant="secondary" className="w-full justify-start">
                Horizon Scanning
              </Button>
              <Button variant="ghost" className="w-full justify-start">
                Action Plans
              </Button>
              <Button variant="ghost" className="w-full justify-start">
                Reports
              </Button>
              <Button variant="ghost" className="w-full justify-start">
                Settings
              </Button>
            </nav>
          </div>
        </Sidebar>

        {/* Main Content */}
        <SidebarInset className="flex-1 p-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Horizon Scanning</h1>
              <p className="text-gray-600 mt-2">
                Monitor regulatory changes, assess impacts, and plan compliance actions
              </p>
            </div>

            {/* Regulation Metadata Header */}
            <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
              <HorizonScanningHeader metadata={horizonScanningData.metadata} />
            </div>

            {/* Filter and Search Bar */}
            <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search by Regulation (e.g., Annex 14)..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex gap-4 w-full md:w-auto">
                  <Select value={impactFilter} onValueChange={setImpactFilter}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by Impact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Impact Levels</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={domainFilter} onValueChange={setDomainFilter}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by Domain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Domains</SelectItem>
                      <SelectItem value="Safety">Safety</SelectItem>
                      <SelectItem value="Operations">Operations</SelectItem>
                      <SelectItem value="Environment">Environment</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    onClick={handleUploadClick}
                    size="sm"
                    className="flex items-center gap-2 hover:bg-accent hover:text-accent-foreground hover:border-[rgba(37,146,1,0.25)] transition-all duration-200 whitespace-nowrap"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Policy or Regulation
                  </Button>
                </div>
              </div>

              {/* Results count */}
              <div className="mt-4 text-sm text-gray-500">
                Showing {filteredItems.length} of {horizonScanningData.items.length} regulatory items
                {searchQuery && ` matching "${searchQuery}"`}
                {impactFilter !== 'all' && ` with ${impactFilter} impact`}
                {domainFilter !== 'all' && ` in ${domainFilter} domain`}
              </div>
            </div>

            {/* Main Grid */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              {filteredItems.length > 0 ? (
                <HorizonScanningGrid items={filteredItems} />
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <Search className="h-12 w-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching regulations found</h3>
                  <p className="text-gray-600">
                    Try adjusting your search or filters to find what you're looking for.
                  </p>
                </div>
              )}
            </div>

            {/* Footer Note */}
            <div className="mt-8 text-center text-sm text-gray-500">
              <p>
                Horizon Scanning data is updated regularly. Last updated: {new Date().toLocaleDateString()}
              </p>
              <p className="mt-1">
                Use the "Add to Action Plan" buttons to create compliance tasks from regulatory changes.
              </p>
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}